/**
 * QuestGenerator.mjs
 * Generates Pattern Quest v2 quests using the template library.
 * Applies feasibility checks, anti-repetition, and scoring.
 */

import { QUEST_TEMPLATES } from './QuestTemplates.mjs';

const TIERS = ['light', 'creative', 'special'];

// Anti-repetition cooldowns
const SIGNATURE_COOLDOWN = 8;   // don't repeat same signature within last 8
const CATEGORY_COOLDOWN  = 3;   // don't repeat same category in last 3 per tier
const TERRAIN_COOLDOWN   = 4;   // don't repeat same primary terrain in last 4

/**
 * Generate a quest for a given tier.
 * @param {'light'|'creative'|'special'} tier
 * @param {object} worldInfo - board state
 * @param {Array<{signature:string, tier:string, category:string, completedAtMs:number}>} history
 * @returns {object} quest instance
 */
export function generateQuest(tier, worldInfo, history) {
  const templates = QUEST_TEMPLATES.filter(t => t.tier === tier);

  // Try to bind each template, collect feasible candidates
  const candidates = [];
  for (const tpl of templates) {
    let bound;
    try { bound = tpl.bind(worldInfo); } catch { continue; }
    if (!bound) continue;

    const score = scoreCandidatex(bound, tpl.category, tier, worldInfo, history);
    if (score === -Infinity) continue;

    candidates.push({ templateId: tpl.id, tier, category: tpl.category, ...bound, score });
  }

  if (candidates.length === 0) {
    return _hardFallback(tier, worldInfo);
  }

  // Sort by score descending, pick from top with weighted random
  candidates.sort((a, b) => b.score - a.score);
  const pool = candidates.slice(0, Math.min(candidates.length, 5));
  const picked = _weightedPick(pool);

  return _buildQuest(tier, picked);
}

// ─── Scoring ─────────────────────────────────────────────────────────────────

function scoreCandidatex(bound, category, tier, worldInfo, history) {
  let score = 0;

  // Anti-repetition: signature cooldown
  const recentSigs = history.slice(-SIGNATURE_COOLDOWN).map(h => h.signature);
  if (recentSigs.includes(bound.signature)) return -Infinity;

  // Anti-repetition: category cooldown per tier
  const tierHistory = history.filter(h => h.tier === tier).slice(-CATEGORY_COOLDOWN);
  const recentCats = tierHistory.map(h => h.category);
  if (recentCats.includes(category)) {
    score -= 20;
  }

  // Anti-repetition: terrain cooldown
  const recentTerrains = history.slice(-TERRAIN_COOLDOWN).map(h => h.terrain).filter(Boolean);
  if (bound.params?.terrain && recentTerrains.includes(bound.params.terrain)) {
    score -= 15;
  }

  // Novelty: prefer categories not recently used
  const allRecentCats = history.slice(-5).map(h => h.category);
  if (!allRecentCats.includes(category)) score += 10;

  // Variety: add some randomness
  score += Math.random() * 20;

  return score;
}

function _weightedPick(pool) {
  // Exponential weighting: best gets weight 1.0, next 0.6, etc.
  const weights = pool.map((_, i) => Math.pow(0.6, i));
  const total = weights.reduce((s, w) => s + w, 0);
  let r = Math.random() * total;
  for (let i = 0; i < pool.length; i++) {
    r -= weights[i];
    if (r <= 0) return pool[i];
  }
  return pool[0];
}

// ─── Hard fallbacks ───────────────────────────────────────────────────────────

function _hardFallback(tier, worldInfo) {
  const ts = Date.now();
  if (tier === 'light') {
    return {
      id: `fallback_light_${ts}`, templateId: 'L1', tier: 'light',
      category: 'expand_world',
      title: 'Soft steps',
      description: 'Place 6 tiles.',
      params: { target: 6 },
      progress: { kind: 'count', current: 0, target: 6 },
      reward: { kind: 'stars', amount: 50 },
      signature: 'expand_world:any:6',
      createdAtMs: ts, completedAtMs: null,
    };
  }
  if (tier === 'creative') {
    return {
      id: `fallback_creative_${ts}`, templateId: 'L3', tier: 'creative',
      category: 'match_edges',
      title: 'New edges, new paths',
      description: 'Create 3 matching edges.',
      params: { target: 3 },
      progress: { kind: 'count', current: 0, target: 3 },
      reward: { kind: 'stars', amount: 75 },
      signature: 'match_edges:3',
      createdAtMs: ts, completedAtMs: null,
    };
  }
  // special fallback: S10 meta
  return {
    id: `fallback_special_${ts}`, templateId: 'S10', tier: 'special',
    category: 'meta',
    title: 'A wonder appears',
    description: 'Complete any 2 quests.',
    params: { target: 2 },
    progress: { kind: 'count', current: 0, target: 2 },
    reward: { kind: 'overlay', pool: 'special', fallbackStars: 100 },
    signature: 'meta:2',
    createdAtMs: ts, completedAtMs: null,
  };
}

// ─── Build final quest object ─────────────────────────────────────────────────

function _buildQuest(tier, bound) {
  const ts = Date.now();
  return {
    id: `${bound.templateId}_${ts}`,
    templateId: bound.templateId,
    tier,
    category: bound.category,
    title: bound.title,
    description: bound.description,
    params: bound.params || {},
    progress: bound.progress,
    reward: bound.reward,
    signature: bound.signature,
    createdAtMs: ts,
    completedAtMs: null,
  };
}
